import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    Landmark,
    ShieldCheck,
    CheckCircle2,
    XCircle,
    Copy,
    ArrowLeft,
    Lock,
} from "lucide-react";
import { toast } from "sonner";

/**
 * SMART Banking Configuration (admin-only, read-only).
 * Displays the platform's settlement/payout account for WiPay + FGB.
 * Values come from backend env vars (PLATFORM_*) so the CEO can rotate
 * without a code change.
 */
export default function AdminBankingPage() {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get("/admin/banking")
            .then(({ data }) => setConfig(data))
            .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
            .finally(() => setLoading(false));
    }, []);

    const copy = (val) => {
        navigator.clipboard.writeText(val).then(() => toast.success("Copied to clipboard"));
    };

    return (
        <div className="min-h-screen bg-background" data-testid="admin-banking-page">
            <Header />
            <section className="max-w-4xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
                <Link
                    to="/admin"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground transition-colors mb-6"
                    data-testid="admin-banking-back"
                >
                    <ArrowLeft size={12} strokeWidth={1.5} /> Admin dashboard
                </Link>

                <div className="mb-8">
                    <div className="flex items-center gap-2 mb-2">
                        <Landmark size={12} className="gold-text" strokeWidth={1.5} />
                        <span className="overline">Payout Settings · Admin only</span>
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">
                        Platform Banking
                    </h1>
                    <p className="mt-3 text-sm text-muted-foreground max-w-lg leading-relaxed">
                        This is the settlement account that receives every platform
                        commission, Royal Protection fee, and shipping charge across
                        SMART DEMANDS 24/7 LIMITED. Read-only — rotate via <code className="gold-text">backend/.env</code>.
                    </p>
                </div>

                {loading && <div className="text-sm text-muted-foreground">Loading configuration…</div>}
                {error && (
                    <div className="p-4 border border-red-900/40 rounded-sm bg-red-900/10 text-sm text-red-300" data-testid="admin-banking-error">
                        {error}
                    </div>
                )}

                {config && (
                    <div
                        className="bg-gradient-to-br from-[#1a1505] via-card to-card border border-[#D4AF37]/40 rounded-md p-6 sm:p-8 relative overflow-hidden"
                        data-testid="admin-banking-card"
                    >
                        {/* Read-only lock badge */}
                        <div className="absolute top-4 right-4 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-amber-300/80 bg-black/40 px-2 py-1 rounded-sm border border-white/10">
                            <Lock size={11} strokeWidth={1.5} /> Read-only
                        </div>
                        <div className="absolute -right-16 -top-16 opacity-10 pointer-events-none">
                            <Landmark size={220} className="gold-text" strokeWidth={0.5} />
                        </div>

                        <div className="relative space-y-6">
                            <Row
                                label="Bank"
                                value={config.bank_name}
                                testid="banking-bank-name"
                                onCopy={() => copy(config.bank_name)}
                            />
                            <Row
                                label="Account Name"
                                value={config.account_name}
                                testid="banking-account-name"
                                onCopy={() => copy(config.account_name)}
                            />
                            <Row
                                label="Account Number"
                                value={config.account_number}
                                display={config.account_number}
                                testid="banking-account-number"
                                onCopy={() => copy(config.account_number)}
                                mono
                            />
                            <Row
                                label="Branch"
                                value={config.branch}
                                testid="banking-branch"
                                onCopy={() => copy(config.branch)}
                            />

                            <div className="hairline" />

                            <div className="flex items-center justify-between" data-testid="banking-wipay-status">
                                <div>
                                    <div className="overline mb-1">WiPay Payouts</div>
                                    <div className="text-sm text-foreground">
                                        {config.wipay_payout_active
                                            ? "Active — settlement running to the account above."
                                            : "Inactive — WiPay payouts NOT running."}
                                    </div>
                                </div>
                                {config.wipay_payout_active ? (
                                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-900/30 border border-emerald-700/40 text-emerald-300 text-[10px] uppercase tracking-[0.22em]" data-testid="wipay-status-active">
                                        <CheckCircle2 size={12} strokeWidth={1.5} /> Live
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-900/25 border border-red-700/40 text-red-300 text-[10px] uppercase tracking-[0.22em]" data-testid="wipay-status-inactive">
                                        <XCircle size={12} strokeWidth={1.5} /> Off
                                    </span>
                                )}
                            </div>

                            <div className="hairline" />

                            <div className="flex items-start gap-3 text-[11px] text-muted-foreground leading-relaxed">
                                <ShieldCheck size={13} className="gold-text shrink-0 mt-0.5" strokeWidth={1.5} />
                                <p>
                                    <strong className="text-foreground">Access:</strong> This page is admin-only.
                                    <strong className="text-foreground"> Rotation:</strong> Edit <code className="gold-text">PLATFORM_ACCOUNT_NUMBER</code>, <code className="gold-text">PLATFORM_ACCOUNT_NAME</code>, or <code className="gold-text">WIPAY_PAYOUT_ACTIVE</code> in <code className="gold-text">backend/.env</code>, then restart the backend.
                                    <strong className="text-foreground"> Audit:</strong> Every payout attempt is logged in <code className="gold-text">payout_items</code>.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </section>
            <Footer />
        </div>
    );
}

function Row({ label, value, display, testid, onCopy, mono }) {
    return (
        <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
                <div className="overline mb-1.5">{label}</div>
                <div
                    className={`text-lg text-foreground truncate ${mono ? "tabular-nums font-mono tracking-wider" : "font-serif"}`}
                    data-testid={testid}
                >
                    {display || value}
                </div>
            </div>
            <button
                type="button"
                onClick={onCopy}
                aria-label={`Copy ${label}`}
                className="grid place-items-center w-10 h-10 rounded-sm border border-white/10 text-muted-foreground hover:text-foreground hover:border-white/30 transition-colors"
                data-testid={`${testid}-copy`}
            >
                <Copy size={14} strokeWidth={1.5} />
            </button>
        </div>
    );
}
